import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useOutletContext } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import {
  Shield,
  LayoutDashboard,
  Users,
  Building2,
  FileText,
  Megaphone,
  Sparkles,
  Globe,
  Mail,
  SlidersHorizontal,
  Lock,
  HardDrive,
  ChevronDown,
  ChevronRight,
  MessageSquareWarning,
} from 'lucide-react';
import { db } from '../../firebase';
import { EcointelLottie } from '../EcoIntelLoader';
import { useUserFacingError } from '../../hooks/useUserFacingError';

/**
 * Grouped admin sub-navigation. Sections that did not exist as their own URL
 * before (e.g. /admin/overview, /admin/announcements) are clearly marked
 * NEW; everything else maps 1:1 to a tab from the legacy AdminDashboard.
 *
 * Putting Operations / Communications / Configuration / System into named
 * groups keeps the rail readable as the number of admin pages grows past
 * the original nine — the UX/UI brief asked us to design for 100+ users
 * and properties so the rail also has to stay scannable when more sections
 * land later.
 */
const NAV_GROUPS = [
  {
    id: 'operations',
    label: 'Operations',
    items: [
      { to: '/admin/overview', label: 'Overview', icon: LayoutDashboard },
      { to: '/admin/users', label: 'Users', icon: Users, countKey: 'users' },
      { to: '/admin/properties', label: 'Properties', icon: Building2, countKey: 'farms' },
      { to: '/admin/reports', label: 'Reports', icon: FileText },
      { to: '/admin/feedback', label: 'Feedback', icon: MessageSquareWarning },
      { to: '/admin/credentials', label: 'Credential review', icon: Shield },
    ],
  },
  {
    id: 'communications',
    label: 'Communications',
    items: [
      { to: '/admin/announcements', label: 'Announcements', icon: Megaphone },
      { to: '/admin/promo-banner', label: 'Promo banner', icon: Sparkles },
      { to: '/admin/email-templates', label: 'Email templates', icon: Mail },
      { to: '/admin/site-content', label: 'Site content', icon: Globe },
    ],
  },
  {
    id: 'configuration',
    label: 'Configuration',
    items: [
      { to: '/admin/views', label: 'View configuration', icon: SlidersHorizontal },
      { to: '/admin/demo', label: 'Demo settings', icon: Lock },
    ],
  },
  {
    id: 'system',
    label: 'System',
    items: [
      { to: '/admin/cache', label: 'Cache', icon: HardDrive },
    ],
  },
];

/**
 * Hook that gives admin pages access to the shared {users, farms, refresh}
 * payload the layout loaded once. Avoids each section refetching the same
 * collections on every navigation.
 */
export function useAdminData() {
  return useOutletContext();
}

function NavItem({ item, count }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      className={({ isActive }) =>
        `flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
          isActive
            ? 'bg-[#043132] text-white shadow-sm'
            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
        }`
      }
    >
      <Icon size={15} className="shrink-0" />
      <span className="flex-1 truncate">{item.label}</span>
      {Number.isFinite(count) ? (
        <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-full">
          {count}
        </span>
      ) : null}
    </NavLink>
  );
}

function NavGroup({ group, defaultOpen, counts }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mb-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 hover:text-slate-600"
      >
        <span>{group.label}</span>
        {open ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
      </button>
      {open ? (
        <div className="space-y-0.5 mt-1">
          {group.items.map((item) => (
            <NavItem key={item.to} item={item} count={item.countKey ? counts[item.countKey] : undefined} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function AdminLayout() {
  const { logError } = useUserFacingError();
  const [users, setUsers] = useState([]);
  const [farms, setFarms] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const uSnap = await getDocs(collection(db, 'users'));
      setUsers(uSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      const fSnap = await getDocs(collection(db, 'farms'));
      setFarms(fSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      logError('AdminLayout fetchData failed', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const counts = useMemo(() => ({ users: users.length, farms: farms.length }), [users, farms]);
  const location = useLocation();

  // Open the group that contains the current route so users always land on
  // an expanded sub-nav after navigation (e.g. deep-link to /admin/cache).
  const activeGroup = useMemo(() => {
    for (const g of NAV_GROUPS) {
      if (g.items.some((item) => location.pathname.startsWith(item.to))) return g.id;
    }
    return NAV_GROUPS[0].id;
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[40vh]">
        <EcointelLottie message="Loading admin console..." lottiePixelWidth={120} lottiePixelHeight={150} />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-[calc(100vh-3.5rem)]">
      {/* Left sub-nav */}
      <aside className="w-60 shrink-0 border-r border-slate-200 bg-white p-3 overflow-y-auto">
        <div className="flex items-center gap-2 px-2 py-2 mb-3">
          <Shield size={18} className="text-[#043132]" />
          <h2 className="font-bold text-sm text-slate-800">Admin Console</h2>
        </div>
        {NAV_GROUPS.map((group) => (
          <NavGroup
            key={group.id}
            group={group}
            defaultOpen={group.id === activeGroup}
            counts={counts}
          />
        ))}
      </aside>

      {/* Active section */}
      <div className="flex-1 min-w-0 overflow-x-hidden">
        <div className="p-6 max-w-7xl mx-auto">
          <Outlet context={{ users, farms, refresh: fetchData }} />
        </div>
      </div>
    </div>
  );
}
